import { useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, ApiError } from "../auth/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { PrivacyNotice } from "../components/PrivacyNotice";
import { pathFor } from "../app/paths";

export function Login() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, resendVerification } = useAuth();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();
  const lang = locale || "it";
  const rawFrom = (location.state as { from?: string })?.from;
  const from = rawFrom && rawFrom.startsWith("/") && !rawFrom.startsWith("//") ? rawFrom : pathFor("collections", locale);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notVerified, setNotVerified] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setNotVerified(false);
    setResent(false);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.message === "email_not_verified") {
        setNotVerified(true);
        setError(t("auth.emailNotVerified"));
      } else {
        setError(err instanceof ApiError ? err.message : t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setResent(false);
    try {
      await resendVerification(email, lang);
    } catch {
      /* risposta sempre uguale (anti-enumeration) */
    }
    setResent(true);
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid rgba(28,75,59,0.89)",
    padding: "14px 18px",
    fontSize: 14,
    boxSizing: "border-box",
    borderRadius: 8,
    outline: "none",
    fontFamily: cfg.font_body,
    color: cfg.color_dark,
    background: cfg.color_body_bg,
    transition: "border-color 0.2s",
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "48px 20px" : "64px 24px",
        background: cfg.color_light_bg,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: cfg.color_body_bg,
          borderRadius: 8,
          padding: isMobile ? "36px 24px" : "48px 40px",
          border: "1px solid rgba(28,75,59,0.89)",
        }}
      >
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          {cfg.logo_url ? (
            <img
              src={resolveImageUrl(cfg.logo_url)}
              alt={cfg.brand_name}
              style={{ height: 36, objectFit: "contain", marginBottom: 16 }}
            />
          ) : (
            <div
              style={{
                fontSize: 20,
                fontWeight: 400,
                color: cfg.color_dark,
                fontFamily: cfg.font_heading,
                letterSpacing: 1,
                marginBottom: 16,
              }}
            >
              {cfg.brand_name}
            </div>
          )}
          <h1
            style={{
              fontSize: isMobile ? 22 : 26,
              fontWeight: 400,
              margin: 0,
              color: cfg.color_dark,
              fontFamily: cfg.font_heading,
            }}
          >
            {t("auth.loginTitle")}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <p
              role="alert"
              style={{
                color: "#991b1b",
                marginBottom: 16,
                fontSize: 13,
                background: "#fef2f2",
                padding: "10px 14px",
                borderRadius: 8,
              }}
            >
              {error}
            </p>
          )}

          {notVerified && (
            <div style={{ marginBottom: 16 }}>
              {resent ? (
                <p style={{ fontSize: 13, color: cfg.color_dark, background: cfg.color_light_bg, padding: "10px 14px", borderRadius: 8, margin: 0 }}>
                  {t("auth.resent")}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    width: "100%",
                    background: "transparent",
                    color: cfg.color_dark,
                    border: "1px solid rgba(28,75,59,0.89)",
                    padding: "12px 24px",
                    cursor: "pointer",
                    fontWeight: 500,
                    borderRadius: 8,
                    fontSize: 12,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {t("auth.resend")}
                </button>
              )}
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                marginBottom: 8,
                color: cfg.color_muted,
              }}
            >
              {t("auth.email")}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                marginBottom: 8,
                color: cfg.color_muted,
              }}
            >
              {t("auth.password")}
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              background: cfg.color_dark,
              color: "#fff",
              border: "none",
              padding: "14px 24px",
              cursor: submitting ? "default" : "pointer",
              fontWeight: 500,
              borderRadius: 8,
              fontSize: 13,
              letterSpacing: 1,
              textTransform: "uppercase",
              opacity: submitting ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (!submitting) e.currentTarget.style.opacity = "1";
            }}
          >
            {submitting ? "..." : t("auth.loginBtn")}
          </button>

          {/* Informativa GDPR (finalità B, C) sotto il login — nessun controllo marketing qui */}
          <div style={{ marginTop: 20 }}>
            <PrivacyNotice variant="auth" />
          </div>
        </form>

        <div
          style={{
            marginTop: 24,
            paddingTop: 24,
            borderTop: "1px solid rgba(28,75,59,0.89)",
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", margin: 0 }}>
            {t("auth.noAccount")}{" "}
            <Link
              to={`/${locale}/signup`}
              style={{ color: cfg.color_dark, textDecoration: "none", fontWeight: 500 }}
            >
              {t("auth.signupLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
